import { useContext, useEffect, useState } from 'react';
import { Feature } from 'geojson';
import { PanelSection } from '../SidePanel';
import { mapModes, SOIL_BOUNDARIES_LAYER_ID } from '../../constants/map';
import { MapContext } from '../../contexts/MapContext';
import { ItemContext } from '../../contexts/ItemContext';
import { MAP_UNIT_DESCRIPTION } from '../../constants/soil-attributes/mapUnitDescription';
import UnderlinedBlueButton from '../basics/UnderlinedBlueButton';
import SoilAttributeTable from '../soil-attributes/SoilAttributeTable';
import SoilAttributeDropdown from '../soil-attributes/SoilAttributeDropdown';
import LearnAboutAttributeButton from '../soil-attributes/LearnAboutAttributeButton';
import SoilDiscreteKey from '../soil-attributes/SoilDiscreteKey';
import { formatSoilFeatureName } from '../../utils/soils';

const SoilLayerMenu = () => {
  const { activeKey } = useContext(ItemContext);
  const {
    // @ts-expect-error MapContext not migrated to TS
    layers: { layers },
    // @ts-expect-error MapContext not migrated to TS
    mapState: { activeLayerFeatureId, selectedLayerFeatureIds, setMapMode },
    soilAttributes: {
      // @ts-expect-error MapContext not migrated to TS
      activeAttribute,
      // @ts-expect-error MapContext not migrated to TS
      activeData,
      // @ts-expect-error MapContext not migrated to TS
      colorKey,
      // @ts-expect-error MapContext not migrated to TS
      legend,
      // @ts-expect-error MapContext not migrated to TS
      setActiveAttribute,
    },
  } = useContext(MapContext);
  const [tableShown, setTableShown] = useState(false);
  const soilLayer = layers[SOIL_BOUNDARIES_LAYER_ID];

  // Reset map colors when closing soil menu
  useEffect(() => {
    return () => setActiveAttribute(MAP_UNIT_DESCRIPTION);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClick = (e: MouseEvent, hexCode: string, label: string) => {
    const layerFeatureIds = soilLayer.data.features
      .filter(
        (f: Feature) =>
          colorKey?.[f.properties?.mapUnitKey] === hexCode ||
          formatSoilFeatureName(f) === label,
      )
      .map((f: Feature) => f.properties?.id);

    // If command is pressed, toggle layer features in selection
    if (e.metaKey || e.ctrlKey) {
      layerFeatureIds.forEach((id: string) =>
        setMapMode(mapModes.MULTI_LAYER_FEATURE_SELECT, id, {
          selectActiveLayerFeature: true,
        }),
      );
      return;
    }
    // Clear existing selection
    setMapMode(mapModes.LAYER_ENGAGED);
    switch (layerFeatureIds.length) {
      case 0:
        return;
      case 1:
        setMapMode(mapModes.LAYER_FEATURE_ENGAGED, [...layerFeatureIds][0]);
        break;
      default:
        layerFeatureIds.forEach((layerFeatureId: string) =>
          setMapMode(mapModes.MULTI_LAYER_FEATURE_SELECT, layerFeatureId),
        );
    }
  };

  if (!soilLayer.data?.features?.length) return null;

  return (
    <>
      <SoilAttributeTable
        activeAttribute={activeAttribute}
        activeLayerFeatureId={activeLayerFeatureId}
        selectedLayerFeatureIds={selectedLayerFeatureIds}
        activeData={activeData}
        soilPolygons={soilLayer.data}
        tableShown={tableShown}
        setTableShown={setTableShown}
      />
      <PanelSection>
        <h4>Soil Attributes</h4>
        <SoilAttributeDropdown
          activeAttribute={activeAttribute}
          setActiveAttribute={setActiveAttribute}
        />
        {activeAttribute && (
          <>
            <div className="d-flex align-items-center justify-content-between">
              <h4 className="mb-0">Key</h4>
              <UnderlinedBlueButton
                color="link"
                onClick={() => setTableShown(true)}
                size="sm"
              >
                Open attribute data
              </UnderlinedBlueButton>
            </div>
            <SoilDiscreteKey
              activeAttribute={activeAttribute}
              activeData={activeData}
              activeKey={activeKey}
              colorKey={colorKey}
              legend={legend}
              data={soilLayer.data}
              onClick={onClick}
            />
            <LearnAboutAttributeButton activeData={activeData} />
          </>
        )}
      </PanelSection>
    </>
  );
};

export default SoilLayerMenu;
